import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * Logowanie operatora — ten sam mechanizm co w aplikacji klienta, inny realm.
 *
 * Realm `backoffice` ma osobne konta, obowiązkowy drugi składnik i krótsze sesje. Adresy wchodzą
 * do wersji przy budowaniu; domyślne są produkcyjne, więc obraz zbudowany bez parametrów jest tym
 * produkcyjnym — i wskazuje realm operatorów, nie użytkowników.
 */
export const config = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? 'https://api.firmeto.pl',
  authIssuer: import.meta.env.VITE_AUTH_ISSUER ?? 'https://auth.firmeto.pl/realms/backoffice',
  clientId: 'firmeto-backoffice',
};

/** Tokeny w pamięci karty, nie w localStorage — wstrzyknięty skrypt nie ma ich skąd wykraść. */
class InMemoryStorage implements Storage {
  private data = new Map<string, string>();

  get length() {
    return this.data.size;
  }

  clear() {
    this.data.clear();
  }

  getItem(key: string) {
    return this.data.get(key) ?? null;
  }

  key(index: number) {
    return [...this.data.keys()][index] ?? null;
  }

  removeItem(key: string) {
    this.data.delete(key);
  }

  setItem(key: string, value: string) {
    this.data.set(key, value);
  }
}

export const AUTH_PATHS = { callback: '/logowanie/powrot', silentRenew: '/logowanie/odnowienie' };

export const userManager = new UserManager({
  authority: config.authIssuer,
  client_id: config.clientId,
  redirect_uri: `${window.location.origin}${AUTH_PATHS.callback}`,
  post_logout_redirect_uri: window.location.origin,
  silent_redirect_uri: `${window.location.origin}${AUTH_PATHS.silentRenew}`,
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: true,
  silentRequestTimeoutInSeconds: 5,
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  userStore: new WebStorageStateStore({ store: new InMemoryStorage() }),
});

/** Adres w panelu jedzie w `state` i wraca po zalogowaniu — link do karty firmy prowadzi do karty. */
export async function signIn(): Promise<void> {
  await userManager.signinRedirect({ state: { path: window.location.pathname } });
}

export async function signOut(): Promise<void> {
  await userManager.signoutRedirect();
}

/** Błędy, które naprawdę znaczą „nie ma sesji" — reszta to sieć albo chwilowa awaria. */
export function meansNoSession(failure: unknown): boolean {
  const code = (failure as { error?: unknown })?.error;
  return (
    typeof code === 'string' &&
    ['login_required', 'interaction_required', 'consent_required', 'account_selection_required'].includes(code)
  );
}

/** Sesja odzyskana po cichu z ciasteczka Keycloaka; null, gdy trzeba się zalogować. */
export async function restoreSession(): Promise<User | null> {
  const current = await userManager.getUser();
  if (current && !current.expired) return current;
  try {
    return await userManager.signinSilent();
  } catch (failure) {
    if (meansNoSession(failure)) return null;
    throw failure;
  }
}
